import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Smartphone, RefreshCcw, Unlink, Loader2 } from "lucide-react";
import { toast } from "sonner";

const btn = "h-9 px-4 rounded-sm text-sm inline-flex items-center gap-2 transition-colors duration-150 disabled:opacity-50";

export const MyWhatsApp = ({ onChange }) => {
  const [me, setMe] = useState(null);
  const [qr, setQr] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try {
      const { data } = await api.get("/whatsapp/me");
      setMe(data);
      if (data.state === "open") setQr(null);
      return data;
    } catch { return null; }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!qr && me?.sync?.status !== "running") return undefined;
    const t = setInterval(async () => { const d = await load(); if (d?.state === "open" && qr) { toast.success("WhatsApp linked — syncing your chat history"); onChange?.(); } }, 4000);
    return () => clearInterval(t);
  }, [qr, me?.sync?.status]); // eslint-disable-line react-hooks/exhaustive-deps
  const link = async () => {
    setBusy(true);
    try { const { data } = await api.post("/whatsapp/me/connect"); if (data.state === "open") { toast.success("Already linked"); load(); } else setQr(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const sync = async () => {
    try { await api.post("/whatsapp/me/sync"); toast.success("Syncing your WhatsApp history in the background"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const unlink = async () => {
    if (!window.confirm("Unlink your WhatsApp from Propzel? Your past chats stay in the CRM.")) return;
    await api.post("/whatsapp/me/disconnect"); setQr(null); load(); onChange?.();
  };
  if (!me) return null;
  const open = me.state === "open";
  const syncing = me.sync?.status === "running";
  return (
    <div className="border border-[#E6E4DD] bg-white rounded-sm p-5 grid lg:grid-cols-[1fr_auto] gap-6" data-testid="wa-my-panel">
      <div className="space-y-3">
        <div className="font-display font-bold text-lg text-forest flex items-center gap-2"><Smartphone className="h-4 w-4" /> My WhatsApp</div>
        <div className="text-sm">Status: <b data-testid="wa-my-state" className={open ? "text-[#2D6A4F]" : "text-clay"}>{!me.linked ? "not linked" : open ? "connected" : me.state.replace(/_/g, " ")}</b>
          {open && me.number && <span className="text-forest/60 tabular-nums"> · +{me.number}</span>}
        </div>
        <div className="text-xs text-forest/60 max-w-xl">Link your own WhatsApp so the messages you send to leads go from your number and every chat on your phone shows up here.{me.can_use_company ? " Until you link, you send from the company number." : " You need to link before you can send WhatsApp messages."}</div>
        {me.sync && <div data-testid="wa-my-sync" className="text-xs text-forest/60">{syncing ? <span className="inline-flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> Syncing history… {me.sync.synced} messages so far</span> : me.sync.status === "done" ? `History synced · ${me.sync.synced} messages` : `Last sync ${me.sync.status}`}</div>}
        <div className="flex flex-wrap gap-2">
          {!open && <button data-testid="wa-my-link" disabled={busy} onClick={link} className={`${btn} bg-[#128C7E] text-white hover:bg-[#0f7a6e]`}>{busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}{me.linked ? "Show QR to reconnect" : "Link my WhatsApp"}</button>}
          {open && <button data-testid="wa-my-sync-btn" disabled={syncing} onClick={sync} className={`${btn} border border-[#E6E4DD] hover:border-forest`}><RefreshCcw className="h-3.5 w-3.5" /> Sync history</button>}
          {me.linked && <button data-testid="wa-my-unlink" onClick={unlink} className={`${btn} border border-[#E6E4DD] text-clay hover:border-clay`}><Unlink className="h-3.5 w-3.5" /> Unlink</button>}
        </div>
      </div>
      {qr && !open && (
        <div className="text-center" data-testid="wa-my-qr">
          {qr.base64 ? <img src={qr.base64} alt="WhatsApp QR" className="h-52 w-52 mx-auto" /> : <div className="h-52 w-52 grid place-items-center text-xs text-forest/50">Generating QR…</div>}
          <div className="text-[11px] text-forest/60 mt-1 max-w-[220px]">On your phone: WhatsApp → Settings → Linked devices → Link a device, then scan.</div>
          {qr.pairing_code && <div className="text-sm mt-1">Code: <b>{qr.pairing_code}</b></div>}
        </div>
      )}
    </div>
  );
};
